import { ConsentGate, type ConsentPromptFn } from 'WebmcpCore/consent/gate';
import { resolveConsentPolicy } from 'WebmcpCore/consent/tier';
import { HandlerError, ValidationError } from 'WebmcpCore/errors';
import { zodToJsonSchemaSafe } from 'WebmcpCore/schema/zod-bridge';
import type { RegisteredTool, SurfaceTier, WebMcpBrowserBinding, WebMcpToolDefinition } from 'WebmcpCore/types';
import { z } from 'zod';

export interface WebMcpRegistryOptions {
  binding: WebMcpBrowserBinding;
  surface: SurfaceTier;
  prompt: ConsentPromptFn;
}

export class WebMcpRegistry {
  private readonly tools = new Map<string, RegisteredTool>();
  private readonly gate: ConsentGate;
  private readonly changeListeners = new Set<() => void>();
  private readonly invokeListeners = new Set<(toolName: string) => void>();

  constructor(private readonly opts: WebMcpRegistryOptions) {
    this.gate = new ConsentGate({ prompt: opts.prompt });
  }

  async register(definition: WebMcpToolDefinition): Promise<string> {
    const policy = resolveConsentPolicy({ surface: this.opts.surface, tool: definition });
    const id = crypto.randomUUID();
    const handle = await this.opts.binding.registerTool({
      description: definition.description,
      handler: async args => {
        this.notifyInvoke(definition.name);
        const parsed = (definition.input as z.ZodTypeAny).safeParse(args);
        if (!parsed.success) {
          throw new ValidationError('Input validation failed', parsed.error.issues);
        }
        await this.gate.check({ args: parsed.data, policy, tool: definition });
        try {
          return await definition.handler(parsed.data);
        } catch (err) {
          if (err instanceof Error) throw new HandlerError(`Handler for ${definition.name} threw`, err);
          throw err;
        }
      },
      inputSchema: zodToJsonSchemaSafe(definition.input),
      name: definition.name,
    });
    this.tools.set(id, { definition, handle, id });
    this.notifyChange();
    return id;
  }

  async unregister(id: string): Promise<void> {
    const entry = this.tools.get(id);
    if (!entry) return;
    this.tools.delete(id);
    this.notifyChange();
    await entry.handle.unregister();
  }

  subscribe(listener: () => void): () => void {
    this.changeListeners.add(listener);
    return () => {
      this.changeListeners.delete(listener);
    };
  }

  onInvoke(listener: (toolName: string) => void): () => void {
    this.invokeListeners.add(listener);
    return () => {
      this.invokeListeners.delete(listener);
    };
  }

  private notifyChange(): void {
    for (const listener of this.changeListeners) {
      listener();
    }
  }

  private notifyInvoke(toolName: string): void {
    for (const listener of this.invokeListeners) {
      listener(toolName);
    }
  }

  listTools(): ReadonlyArray<WebMcpToolDefinition> {
    return Array.from(this.tools.values()).map(t => t.definition);
  }

  async disposeAll(): Promise<void> {
    const ids = Array.from(this.tools.keys());
    await Promise.all(ids.map(id => this.unregister(id)));
  }
}
